import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { ErrorMessage } from './errors/ErrorMessage'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  title?: string
  message?: string | ReactNode
  onReset?: () => void
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Don't report AbortErrors - they're expected
    if (error.name === 'AbortError') {
      return
    }

    // Report to Sentry
    Sentry.captureException(error, {
      tags: {
        errorType: 'error-boundary',
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
    })

    console.error('Error caught by boundary:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
    this.props.onReset?.()
  }

  override render() {
    if (this.state.hasError) {
      // Don't show error UI for AbortErrors
      if (this.state.error?.name === 'AbortError') {
        return this.props.children
      }

      if (this.props.fallback) {
        return this.props.fallback
      }

      // If title or message provided, use ErrorMessage component
      if (this.props.title || this.props.message) {
        return (
          <ErrorMessage
            title={this.props.title || 'Něco se pokazilo'}
            message={
              this.props.message ||
              this.state.error?.message ||
              'Omlouváme se, ale nepodařilo se načíst obsah. Zkuste to prosím znovu.'
            }
            onDismiss={this.props.onReset ? this.handleReset : undefined}
          />
        )
      }

      // Default fallback UI
      return (
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="max-w-md rounded-lg bg-red-50 p-6 text-center">
            <h2 className="mb-2 text-xl font-bold text-red-800">Něco se pokazilo</h2>
            <p className="mb-4 text-red-600">{this.state.error?.message || 'Neočekávaná chyba'}</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
            >
              Obnovit stránku
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
